import React, { useRef, useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Alert,
  AlertIcon,
  useToast,
  useColorModeValue,
  Icon,
  Divider,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Heading,
  Spinner,
  Center,
  Code,
} from '@chakra-ui/react';
import { FiUser, FiUsers } from 'react-icons/fi';
import {
  useCrmDefaultOwnerScope,
  CRM_OWNER_SCOPE,
} from '../../../hooks/useCrmDefaultOwnerScope';

export default function CrmViewPreferences({ userId, targetUser }) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);

  // A click on the card bubbles from the Radio's own change handler, so a
  // single click can invoke handleChange twice. React batches state updates
  // within one event, so only a ref reliably blocks the second call.
  const inFlightRef = useRef(false);

  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const cardBG = useColorModeValue('gray.50', 'gray.700');
  const selectedBG = useColorModeValue('blue.50', 'blue.900');

  const { defaultOwnerScope, loading, updateDefaultOwnerScope } = useCrmDefaultOwnerScope(userId);

  const targetIsCrmAdmin =
    targetUser?.SuperAdmin === true ||
    (Array.isArray(targetUser?.Roles) && targetUser.Roles.includes('CRM Admin'));

  const handleChange = async (nextScope) => {
    if (inFlightRef.current || nextScope === defaultOwnerScope) return;
    inFlightRef.current = true;
    setSaving(true);
    try {
      await updateDefaultOwnerScope(nextScope);
      toast({
        title: 'Default view updated',
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      console.error('Error updating CRM default view:', error);
      toast({
        title: 'Error updating default view',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      inFlightRef.current = false;
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Center py={8}>
        <Spinner />
      </Center>
    );
  }

  const isSelf = defaultOwnerScope === CRM_OWNER_SCOPE.SELF;
  const isAll = defaultOwnerScope === CRM_OWNER_SCOPE.ALL;

  return (
    <VStack align="stretch" spacing={6}>
      <Box>
        <Heading size="md" mb={2}>Default View</Heading>
        <Text fontSize="sm" color="gray.600">
          Choose whose records the CRM Dashboard and My Deals pages open on
        </Text>
      </Box>

      <Divider />

      {/* Wait for targetUser so the alert never flashes before roles are known */}
      {targetUser && !targetIsCrmAdmin && (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <VStack align="flex-start" spacing={1} flex={1}>
            <Text fontSize="sm" fontWeight="semibold">
              Applies to CRM Admins only
            </Text>
            <Text fontSize="xs">
              This user does not have CRM Admin access, so their Dashboard and My Deals pages
              always show only their own contacts and deals.
            </Text>
          </VStack>
        </Alert>
      )}

      <FormControl>
        <FormLabel fontSize="sm" fontWeight="semibold" mb={3}>
          When Dashboard or My Deals opens, show
        </FormLabel>
        <RadioGroup value={defaultOwnerScope} onChange={handleChange}>
          <VStack align="stretch" spacing={3}>
            {/* My records */}
            <Box
              p={4}
              bg={isSelf ? selectedBG : cardBG}
              borderWidth="2px"
              borderColor={isSelf ? 'blue.400' : borderColor}
              borderRadius="lg"
              cursor="pointer"
              onClick={() => handleChange(CRM_OWNER_SCOPE.SELF)}
              transition="all 0.2s"
              _hover={{ borderColor: 'blue.300', shadow: 'sm' }}
            >
              <HStack spacing={3}>
                <Radio value={CRM_OWNER_SCOPE.SELF} colorScheme="blue" isDisabled={saving} />
                <Icon as={FiUser} boxSize={6} color="blue.500" />
                <VStack align="flex-start" spacing={0} flex={1}>
                  <Text fontWeight="semibold" fontSize="sm">My records</Text>
                  <Text fontSize="xs" color="gray.600">
                    Opens filtered to your own contacts and deals. You can still switch to any
                    other user from the dropdown.
                  </Text>
                </VStack>
                {isSelf && <Badge colorScheme="blue">Selected</Badge>}
              </HStack>
            </Box>

            {/* All users */}
            <Box
              p={4}
              bg={isAll ? selectedBG : cardBG}
              borderWidth="2px"
              borderColor={isAll ? 'blue.400' : borderColor}
              borderRadius="lg"
              cursor="pointer"
              onClick={() => handleChange(CRM_OWNER_SCOPE.ALL)}
              transition="all 0.2s"
              _hover={{ borderColor: 'blue.300', shadow: 'sm' }}
            >
              <HStack spacing={3}>
                <Radio value={CRM_OWNER_SCOPE.ALL} colorScheme="blue" isDisabled={saving} />
                <Icon as={FiUsers} boxSize={6} color="purple.500" />
                <VStack align="flex-start" spacing={0} flex={1}>
                  <Text fontWeight="semibold" fontSize="sm">All users</Text>
                  <Text fontSize="xs" color="gray.600">
                    Opens on the team-wide view with "All Users" selected.
                  </Text>
                </VStack>
                {isAll && <Badge colorScheme="blue">Selected</Badge>}
              </HStack>
            </Box>
          </VStack>
        </RadioGroup>
      </FormControl>

      <Box
        fontSize="xs"
        color="gray.500"
        p={4}
        bg={cardBG}
        borderRadius="md"
        borderWidth="1px"
        borderColor={borderColor}
      >
        <Text fontWeight="semibold" mb={2}>About this setting</Text>
        <VStack align="flex-start" spacing={1}>
          <Text>
            • A link that already names a user (a URL with <Code fontSize="xs">?userId=</Code>)
            always overrides this default
          </Text>
          <Text>
            • Changes apply the next time you open the Dashboard or My Deals
          </Text>
          <Text>
            • Tasks, anniversaries and other personal widgets always stay on your own records
          </Text>
        </VStack>
      </Box>
    </VStack>
  );
}
